import { motion } from 'framer-motion';

// Inline SVG icons for each solution card
const icons = [
  // Messaging
  <svg key="msg" className="w-8 h-8 mb-2 text-cyan-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h5m-5 4h8m-3 8l-4-4H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v11a2 2 0 01-2 2h-3l-4 4z"/>
  </svg>,
  // Automation
  <svg key="auto" className="w-8 h-8 mb-2 text-orange-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m-4 4a8 8 0 110-16 8 8 0 010 16zm0 0v4m0 0H8m4 0h4"/>
  </svg>,
  // Custom Agent
  <svg key="custom" className="w-8 h-8 mb-2 text-cyan-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="4"/><path strokeLinecap="round" strokeLinejoin="round" d="M4.93 19.07a10 10 0 1114.14 0"/>
  </svg>,
  // Booking
  <svg key="booking" className="w-8 h-8 mb-2 text-orange-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <rect x="3" y="8" width="18" height="13" rx="2"/><path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18"/>
  </svg>,
  // Insight
  <svg key="insight" className="w-8 h-8 mb-2 text-cyan-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 20V10m6 10V4M6 20v-6"/>
  </svg>,
  // Integrator
  <svg key="integrate" className="w-8 h-8 mb-2 text-orange-300" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
    <circle cx="6" cy="12" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="18" cy="18" r="3"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.59 10.59l6.83-4.08M8.59 13.41l6.83 4.08"/>
  </svg>
];

export default function LandingPage() {
  const demoBookingLink = "https://calendly.com/mockwit-support/30min";

  // Capability "cards"
  const solutions = [
    {
      title: "Agentic Messaging",
      accent: "text-cyan-300 drop-shadow-glow",
      desc: "Connect with customers on WhatsApp, Messenger, SMS, and more.",
      bullets: [
        "Bookings, reminders, and support on any channel",
        "Conversational AI for natural, human-like interactions",
        "Seamless integration with your CRM and workflows"
      ]
    },
    {
      title: "Automation Flows",
      accent: "text-orange-300 drop-shadow-glow-orange",
      desc: "Automate routine business processes end-to-end.",
      bullets: [
        "Order processing, scheduling, and notifications",
        "Trigger multi-step workflows across your tools",
        "Reduce manual effort and human error"
      ]
    },
    {
      title: "Custom AI Agents",
      accent: "text-cyan-300 drop-shadow-glow",
      desc: "Tailored solutions for your unique business needs.",
      bullets: [
        "Any workflow or process, internal or external",
        "Dashboard and API integration",
        "From scoping to deployment—full support"
      ]
    },
    {
      title: "Conversational Booking",
      accent: "text-orange-300 drop-shadow-glow-orange",
      desc: "Let customers book appointments or services instantly via chat.",
      bullets: [
        "24/7 availability across channels",
        "Automated confirmations and reminders",
        "Smart rescheduling and follow-up"
      ]
    },
    {
      title: "Insight & Data Agent",
      accent: "text-cyan-300 drop-shadow-glow",
      desc: "Turn data into action with AI-driven reporting and Q&A.",
      bullets: [
        "On-demand dashboards via chat or web",
        "Automated status updates and alerts",
        "Predictive analytics for smarter decisions"
      ]
    },
    {
      title: "Workflow Integrator",
      accent: "text-orange-300 drop-shadow-glow-orange",
      desc: "Connect all your business tools—no code required.",
      bullets: [
        "Sync data between apps automatically",
        "Trigger actions based on chat, email, or webhooks",
        "Flexible and secure, with full audit trails"
      ]
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-tr from-cyan-500/80 via-blue-600 to-cyan-950 font-sans text-cyan-50 overflow-x-hidden">
      {/* Bright, animated background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="w-full h-full animate-gradient-x bg-gradient-to-tr from-cyan-300/50 via-cyan-900/90 to-blue-800/90 opacity-95" />
        <div
          className="absolute inset-0"
          style={{
            opacity: 0.16,
            backgroundImage:
              'radial-gradient(circle, #22d3ee 2px, transparent 1.8px)',
            backgroundSize: '22px 22px'
          }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 flex justify-between items-center px-4 sm:px-8 py-3 sm:py-4 bg-cyan-950/80 border-b border-cyan-400/20 shadow-xl backdrop-blur-lg">
        <div className="flex items-center gap-3">
          <span className="font-extrabold font-mono tracking-wider text-cyan-200 drop-shadow-glow
            text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl
            transition-all duration-200"
          >
            MockWit.ai
          </span>
          <span className="hidden md:inline-block text-cyan-100/90 text-base ml-4 font-mono">
            Agentic Automation for Modern Business
          </span>
        </div>
        <div className="flex gap-2 sm:gap-4 items-center">
          <a
            href="#start"
            className="w-28 sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-2xl text-base sm:text-lg font-semibold bg-cyan-400 hover:bg-cyan-300 text-cyan-900 font-mono shadow-xl border border-cyan-200/60 transition animate-glow-pulse overflow-hidden text-center"
          >
            <span className="absolute inset-0 rounded-2xl border-2 border-cyan-200 blur-[2px] opacity-20 animate-pulse pointer-events-none hidden sm:block"></span>
            Get Started
          </a>
        </div>
      </header>

      {/* SVG WAVE Divider */}
      <svg className="w-full h-10 md:h-16 z-10 relative -mb-1" viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill="#22d3ee" fillOpacity="0.16" d="M0,0 C480,100 960,0 1440,100 L1440,100 L0,100 Z"></path>
      </svg>

      {/* HERO */}
      <motion.section
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.7 }}
        transition={{ duration: 0.85, ease: 'easeOut' }}
        className="z-10 relative flex flex-col items-center text-center pt-14 pb-10 px-4 md:px-0"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-5 font-mono tracking-wide text-cyan-50 drop-shadow-glow">
          Transform Your Business with
          <span className="text-cyan-300"> AI-Powered Agents & Automation Flows</span>
        </h1>
        <p className="max-w-2xl text-lg text-cyan-100/95 mb-7 font-mono">
          MockWit.ai builds and integrates custom AI agents that automate processes, streamline operations, and connect you with customers—on WhatsApp, Messenger, SMS, and beyond. 
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto justify-center items-center">
          <a
            href={demoBookingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-block px-6 sm:px-8 py-3 rounded-xl font-bold text-base sm:text-lg bg-orange-400 hover:bg-orange-300 active:bg-orange-500 text-cyan-900 font-mono shadow-lg border border-orange-200/60 transition active:scale-95 text-center"
          >
            Book a Free Demo
          </a>
          <a
            href="mailto:support@mockwit.ai"
            className="w-full sm:w-auto inline-block px-6 sm:px-8 py-3 rounded-xl font-bold text-base sm:text-lg bg-cyan-400 hover:bg-cyan-300 active:bg-cyan-500 text-cyan-900 font-mono shadow-lg border border-cyan-200/60 transition active:scale-95 text-center"
          >
            Contact Us
          </a>
        </div>
      </motion.section>

      {/* WHY MOCKWIT */}
      <motion.section
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.45 }}
        transition={{ duration: 0.75, delay: 0.09 }}
        className="z-10 relative py-10 max-w-3xl mx-auto text-center"
      >
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-orange-300 font-mono drop-shadow-glow-orange">
          Why Choose MockWit?
        </h2>
        <ul className="text-lg text-cyan-100 font-mono flex flex-col gap-2 mb-3">
          <li>
            <span className="text-cyan-400 font-bold">All-in-one automation partner:</span> From scoping to deployment and ongoing support, we handle it all.
          </li>
          <li>
            <span className="text-cyan-400 font-bold">Omnichannel agents:</span> WhatsApp, Messenger, SMS, Email, Web Chat, and more.
          </li>
          <li>
            <span className="text-cyan-400 font-bold">Works with your tools:</span> Easy integrations with your CRM, calendar, inventory, and APIs.
          </li>
          <li>
            <span className="text-cyan-400 font-bold">Custom to your workflow:</span> Every business is unique—we tailor agents and flows for your exact needs.
          </li>
        </ul>
      </motion.section>

      {/* SOLUTIONS/USE CASES with icons */}
      <section className="z-10 relative grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-0 max-w-6xl mx-auto">
        {solutions.map((sol, idx) => (
          <motion.div
            key={sol.title}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.17 }}
            transition={{ duration: 0.65, delay: 0.06 * idx }}
            className="rounded-2xl border backdrop-blur-md bg-cyan-900/50 border-cyan-400/40 shadow-xl shadow-cyan-400/30 p-8 hover:scale-[1.03] hover:shadow-cyan-300/50 hover:border-cyan-200 transition-all duration-300 flex flex-col items-start"
          >
            {icons[idx]}
            <h3 className={`text-xl md:text-2xl font-bold mb-2 font-mono ${sol.accent}`}>{sol.title}</h3>
            <p className="text-cyan-100 mb-3 font-mono">{sol.desc}</p>
            <ul className="list-disc list-inside text-cyan-100/90 font-mono mt-1 ml-3">
              {sol.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </motion.div>
        ))}
      </section>

      {/* SOCIAL PROOF / INNOVATORS */}
      <div className="text-center mt-12 text-cyan-100/90 font-mono text-sm">
        Join innovators in <span className="text-orange-400 font-semibold">services, retail, logistics, healthcare, SaaS</span> and more—already using agentic automation.
      </div>

      {/* STRONG CTA */}
      <motion.section
        id="start"
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.85, delay: 0.12 }}
        className="z-10 relative mt-16 flex flex-col items-center text-center px-4 md:px-0"
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-orange-300 font-mono drop-shadow-glow-orange">
          See Agentic Automation in Action
        </h2>
        <p className="text-cyan-100 max-w-2xl mb-5 font-mono">
          Want to automate workflows, bookings, or customer engagement on WhatsApp, Messenger, and more? 
          <span className="block sm:inline"> </span>
          <span className="block sm:inline">Book a free consultation or contact us for a demo of MockWit’s agentic solutions for your business.</span>
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto justify-center items-center">
          <a
            href={demoBookingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-block px-6 sm:px-8 py-3 rounded-xl font-bold text-base sm:text-lg bg-orange-400 hover:bg-orange-300 active:bg-orange-500 text-cyan-900 font-mono shadow-lg border border-orange-200/60 transition active:scale-95 text-center"
          >
            Book My Demo
          </a>
          <a
            href="mailto:support@mockwit.ai"
            className="w-full sm:w-auto inline-block px-6 sm:px-8 py-3 rounded-xl font-bold text-base sm:text-lg bg-cyan-400 hover:bg-cyan-300 active:bg-cyan-500 text-cyan-900 font-mono shadow-lg border border-cyan-200/60 transition active:scale-95 text-center"
          >
            Contact Us
          </a>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="z-10 relative mt-20 pb-8 pt-12 px-4 text-center text-cyan-200/90 text-sm border-t border-cyan-200/30 bg-cyan-950/80 backdrop-blur-lg">
        <div className="mb-2">
          &copy; {new Date().getFullYear()} <span className="text-cyan-100 font-mono drop-shadow-glow">MockWit.ai</span>. All rights reserved.
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-6 justify-center items-center">
          <a href="mailto:support@mockwit.ai" className="hover:underline text-cyan-50 font-mono">Contact</a>
          <a href="/privacy" className="hover:underline text-cyan-50 font-mono">Privacy Policy</a>
          <a href="/terms" className="hover:underline text-cyan-50 font-mono">Terms of Use</a>  
          <span className="opacity-80">Made with <span className="text-pink-300">❤️</span> by AI innovators</span>
        </div>
        <div className="mt-2 text-orange-200 font-semibold font-mono drop-shadow-glow-orange">
          Let’s build your first agent!
        </div>
      </footer>
    </div>
  );
}
